import { useContext, useEffect, useRef, useState } from "react";
import { I18nContext } from "../../context/i18n.context";
import { GetlifeContext, useQuery } from "../../context/GetLifeContext.context";
import HttpLeadRepository from "../../api/request/Lead/Lead.service";
import {
  FacileBasePriceI,
  FacileLeadI,
  PostFacileLeadI,
  PostFacileOrganicI,
} from "../../api/request/Lead/Interfaces/DataInterface.response";
import { GetLifeTrackerContext } from "../../context/GetLifeTracker.context";
import { useNavigate } from "react-router";
import { formatPhoneWithCallCode } from "../../utils/InternationlChanges";
import { hashIdentity } from "../../utils/hashIdentity";
import { sendPartnerLandingHit } from "../../helpers/Tracking/partnerLandingHit";
import { expectLeadIdentification } from "../../helpers/Tracking";
import { OrganicRequestI } from "../../api/request/Lead/Interfaces/OrganicInterface";
import { organicNativeEnabled } from "../../native/nativeMode";
import {
  facileAttribution,
  facileBirthDate,
  facileGender,
} from "../../native/facileNative";
import {
  isConsentCaptureEnabled,
  stashPendingConsents,
} from "../../utils/consentCapture";
import { buildCalculatorConsents } from "../../components/HealthDataConsent/consentTexts";
import { DOMAIN } from "../../constants/Global";

const FacilePartnerController = () => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const navigate = useNavigate();
  let query = useQuery();
  const {
    setLeadId,
    setToken,
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    utm_test,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion,
    handleCallMe
  } = useContext(GetlifeContext);

  const leadRepository = new HttpLeadRepository();

  const { handleTrackerQuestion, identifyFS } = useContext(
    GetLifeTrackerContext
  );

  const token = query.get("TOKEN") || query.get("token");

  const [leadData, setLeadData] = useState<FacileLeadI>();
  const [form, setForm] = useState<{
    email: string;
    phone: string;
    conditions: boolean;
    conditions2: boolean;
    conditions3: boolean;
  }>({
    email: "",
    phone: "",
    conditions: false,
    conditions2: false,
    conditions3: false,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [prices, setPrices] = useState<FacileBasePriceI>();
  const landingHitSent = useRef(false);

  // Build native: el lead nace en el gateway como cualquier organic (sin
  // monolito) y sigue por el mismo paso que el calculador organic native.
  const sendDataNative = async (form: any) => {
    const { email, phone, conditions3 } = form;
    const attribution = facileAttribution(leadData!.externalReference);
    const organic = await leadRepository.postLeadOrganic({
      birthDate: facileBirthDate(leadData!.dateOfBirth),
      birthdate: facileBirthDate(leadData!.dateOfBirth),
      gender: facileGender(leadData!.gender),
      capital: Number(leadData!.capital),
      nicotine: leadData!.nicotine ? "yes" : "no",
      email,
      phone,
      firstNameRegister: leadData!.name,
      lastNameRegister: leadData!.lastName,
      campaign: attribution.campaign,
      userAcceptsCommunications: conditions3,
    } as OrganicRequestI);
    const leadId = organic.leadId;

    // Consentimientos de las casillas (mismos textos que el calculador IT):
    // pendientes hasta el cuestionario de salud, como en el calculador.
    if (isConsentCaptureEnabled()) {
      try {
        stashPendingConsents(
          leadId,
          await buildCalculatorConsents(translate, true === conditions3, DOMAIN)
        );
      } catch {
        // sin sessionStorage o sin crypto.subtle: se pierde el registro, no el alta
      }
    }

    const identity = await hashIdentity({
      email,
      phone: formatPhoneWithCallCode(phone),
    });
    const dataInfo = {
      ...identity,
      calculatedCapital: Number(leadData!.capital),
      calculatedPremium: organic.basicPrice,
      coverage: organic.coverage === "premium" ? "FIPA" : "F",
      intent: undefined,
      leadId,
      page: window.location.href,
      postalCode: undefined,
      utm_source: attribution.utmSource,
      utm_campaign: utm_campaign,
      utm_medium: attribution.utmMedium,
      utm_content: utm_content,
      utm_term: utm_term,
      utm_test: utm_test,
      gclid,
      gbraid,
      wbraid,
      msclkid,
      fbclid,
      platform: "App",
      entryPage: getEntryPage(),
      referrerUrl: getReferrerUrl(),
      ABTestVersion: getABTestVersion(),
    };

    expectLeadIdentification("partner");
    setLeadId(leadId);
    setToken("");

    identifyFS({
      lead: leadId,
      data: {
        emailDomain: email && email.split("@")[1],
        email: email,
        brokerId: "null",
      },
    });

    // newLead solo cuando Identity crea la persona (como el calculador native).
    if (organic.isFirstIncoming) {
      handleTrackerQuestion({
        type: "FS",
        event: "leadCreated",
        data: { birthDate: new Date(leadData!.dateOfBirth), ...dataInfo },
      });
      handleTrackerQuestion({
        type: "GA",
        data: { event: "leadCreated", birthDate: leadData!.dateOfBirth, ...dataInfo },
      });
    }

    handleTrackerQuestion({
      type: "tracker",
      data: {
        leadUlid: leadId,
        campaign: attribution.campaign,
        utmSource: attribution.utmSource,
        utmMedium: attribution.utmMedium,
        utmCampaign: attribution.campaign,
      },
    });

    navigate(`/calculator/4?lead=${leadId}&gtf_campaign=facile`);
  };

  const sendData = async (form: any) => {
    if (loading) return;

    setLoading(true);

    if (organicNativeEnabled()) {
      await sendDataNative(form)
        .catch((e) => console.log(e))
        .finally(() => setLoading(false));
      return;
    }

    const { email, phone, conditions3 } = form;
    const data: PostFacileLeadI = {
      name: leadData!.name,
      lastName: leadData!.lastName,
      birthDate: leadData!.dateOfBirth,
      capital: leadData!.capital,
      nicotine: leadData!.nicotine ? "yes" : "no",
      gender: leadData!.gender,
      email,
      phone,
      userAcceptsCommunications: conditions3
    };

    leadRepository
      .postFacileOrganic(data)
      .then(async (response: PostFacileOrganicI) => {
        const { leadId, coverage, capital } = response;

        const identity = await hashIdentity({
          email: data.email,
          phone: formatPhoneWithCallCode(data.phone),
        });
        const dataInfo = {
          ...identity,
          calculatedCapital: capital,
          calculatedPremium: prices!.monthly.promotedPrice,
          coverage: coverage === "premium" ? "FIPA" : "F",
          intent: undefined,
          leadId: leadId,
          page: window.location.href,
          postalCode: undefined,
          utm_source: leadData!.externalReference ? "facile" : utm_source,
          utm_campaign: utm_campaign,
          utm_medium: leadData!.externalReference ? "affiliate" : utm_medium,
          utm_content: utm_content,
          utm_term: utm_term,
          utm_test: utm_test,
          gclid: gclid,
          gbraid: gbraid,
          wbraid: wbraid,
          msclkid: msclkid,
          fbclid: fbclid,
          platform: "App",
          entryPage: getEntryPage(),
          referrerUrl: getReferrerUrl(),
          ABTestVersion: getABTestVersion(),
        };

        expectLeadIdentification("partner");
        setLeadId(leadId);
        setToken("");

        identifyFS({
          lead: leadId,
          data: {
            emailDomain: email && email.split("@")[1],
            email: email,
            brokerId: "null",
          },
        });

        handleTrackerQuestion({
          type: "FS",
          event: "leadCreated",
          data: {
            birthDate: new Date(data.birthDate),
            ...dataInfo,
          },
        });
        handleTrackerQuestion({
          type: "GA",
          data: {
            event: "leadCreated",
            birthDate: data.birthDate,
            ...dataInfo,
          },
        });

        handleTrackerQuestion({
          type: "tracker",
          data: {
            leadUlid: leadId,
            campaign: leadData!.externalReference ? leadData!.externalReference : "landing-facile",
            utmSource: leadData!.externalReference && "facile",
            utmMedium: leadData!.externalReference && "affiliate",
          },
        });

        navigate(`/calculator/4?lead=${leadId}&gtf_campaign=facile`);
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  };

  // Facile cobra por clic: el CRM cuenta la visita en cuanto hay token, antes
  // e independientemente de que getPartnerFacile lo acepte (como el $pageview
  // de PostHog). El ref evita el doble disparo del doble montaje de StrictMode
  // (el CRM deduplica por clickId de todos modos).
  useEffect(() => {
    if (!token || landingHitSent.current) return;
    landingHitSent.current = true;
    sendPartnerLandingHit({
      partner: "facile",
      landing: "form",
      clickId: token,
      path: "/partners/facile",
    });
  }, [token]);

  useEffect(() => {
    (async () => {
      if (token) {
        await leadRepository
          .getPartnerFacile(token)
          .then(async (response) => {
            const {
              dateOfBirth,
              gender,
              capital,
              nicotine,
              email,
              phoneNumber,
            } = response;
            setForm({ ...form, email, phone: phoneNumber });
            setLeadData(response);
            const basePrices = await leadRepository.getPartnerFacilePrice({
              dateOfBirth,
              gender,
              capital,
              nicotine,
            });
            setPrices(basePrices);
          })
          .catch((e) => navigate("/calculator/1"));
      }
      if (!token) navigate("/calculator/1");
    })();
  }, [token]);

  return { translate, leadData, form, sendData, prices, handleCallMe };
};

export default FacilePartnerController;
